(() => {
  const normalise = (value) => value.replace(/\s+/g, ' ').replace(/,(?=[a-z])/gi, ', ').trim();
  // Kit labels contain both the total and a per-module amount. Count each
  // selected kit once when the structured capacity is not available yet.
  const memoryCapacityFromLabel = (value) => String(value || '').split(/\s+\+\s+/).reduce((total, part) => {
    const withoutDetails = part.replace(/\([^)]*\)/g, '');
    const kit = part.match(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)\s*GB/i);
    const amount = withoutDetails.match(/(\d+(?:\.\d+)?)\s*GB/i);
    const onlyModules = /\d+\s*[x×]\s*\d+(?:\.\d+)?\s*GB/i.test(withoutDetails);
    return total + (amount && !onlyModules ? Number(amount[1]) : kit ? Number(kit[1]) * Number(kit[2]) : 0);
  }, 0);
  const isPlaceholder = (value) => /^(?:no descr\S*(?:\s+\S+){0,3}|n\/?a|none|tbd|(?:product )?description coming soon)[.!]?$/i.test(value);
  const joinList = (value) => {
    const items = value.split(/,(?!\d)/).map((item) => item.trim()).filter(Boolean);
    return items.length < 2 ? value : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`;
  };
  const describeFact = (fact) => {
    const separator = fact.indexOf(':');
    if (separator < 0) return `${fact}.`;
    const label = fact.slice(0, separator).trim().toLowerCase();
    const value = fact.slice(separator + 1).trim();
    switch (label) {
      case 'features':
      case 'key features': return `Features include ${joinList(value)}.`;
      case 'maximum dpi': return `Maximum sensitivity is ${value}${/dpi/i.test(value) ? '' : ' DPI'}.`;
      case 'connectivity': return `Connectivity options include ${joinList(value)}.`;
      case 'number of buttons': return `It has ${value} buttons.`;
      case 'capacity':
      case 'memory size': return `Capacity is ${value}.`;
      case 'memory type': return `It uses ${value} memory.`;
      case 'clock frequency': return `The clock frequency is ${value}.`;
      case 'form factor': return `The form factor is ${value}.`;
      case 'socket': return `Socket support is listed as ${value}.`;
      case 'interface': return `The interface is ${value}.`;
      case 'chipset': return `It uses the ${value} chipset.`;
      case 'tracking method': return `It uses ${value} tracking.`;
      case 'colour':
      case 'color': return `Colour: ${value}.`;
      default: return `${fact}.`;
    }
  };

  const readText = (element) => {
    // Specification sources may live in an inactive information tab on mobile.
    if (element.closest?.('script, style, template, noscript, [hidden]:not([data-product-tab-panel])')) return '';
    const copy = element.cloneNode(true);
    copy.querySelectorAll('script, style, template, noscript, [hidden]').forEach((node) => node.remove());
    copy.querySelectorAll('br').forEach((node) => node.replaceWith(' '));
    copy.querySelectorAll('p, div, li, dt, dd, td, th').forEach((node) => node.append(' '));
    return normalise(copy.textContent || '');
  };

  const summarise = (sources, title) => {
    const prose = [];
    const facts = [];
    const seen = new Set([normalise(title).toLowerCase()]);
    const add = (target, value) => {
      const text = normalise(value);
      const key = text.toLowerCase();
      if (!text || isPlaceholder(text) || seen.has(key)) return;
      seen.add(key);
      target.push(text);
    };

    sources.forEach((source) => {
      source.querySelectorAll('p').forEach((paragraph) => {
        if (!paragraph.closest('li, td, th, dd') && !paragraph.querySelector('table, ul, ol')) {
          const text = readText(paragraph);
          add(text.includes(':') && text.length <= 180 ? facts : prose, text);
        }
      });
      source.querySelectorAll('li, tr, dt').forEach((row) => {
        if (row.matches('tr')) {
          const cells = Array.from(row.children).filter((cell) => cell.matches('td, th'));
          if (cells.length < 2) return;
          const values = cells.map(readText).filter(Boolean);
          if (values.length > 1) add(facts, `${values[0].replace(/:$/, '')}: ${values.slice(1).join(', ')}`);
        } else if (row.matches('dt')) {
          const value = row.nextElementSibling;
          if (value?.matches('dd')) add(facts, `${readText(row).replace(/:$/, '')}: ${readText(value)}`);
        } else if (!row.querySelector('ul, ol, table')) {
          add(facts, readText(row));
        }
      });
      if (!source.querySelector('p, li, tr, dt')) add(prose, readText(source));
    });

    // Keep complete source sentences and complete specification values.
    const sentences = prose.flatMap((paragraph) => paragraph.match(/.+?(?:[.!?](?=\s|$)|$)/g) || [paragraph]);
    const overview = [];
    for (const sentence of sentences) {
      const text = normalise(sentence);
      if (text && overview.join(' ').length + text.length <= 360) overview.push(text);
      if (overview.length === 2) break;
    }

    const rankedFacts = facts.filter((fact) => !/^(?:country of origin|unit (?:type|quantity)|mpn|ean|upc|sku|brand|model|manufacturer warranty)\s*:/i.test(fact));
    const rank = (fact) => {
      const label = fact.split(':')[0].toLowerCase();
      if (/features|key features/.test(label)) return 0;
      if (/capacity|memory size|memory type|maximum dpi|chipset|power|wattage/.test(label)) return 1;
      if (/connectivity|interface|socket|form factor/.test(label)) return 2;
      if (/speed|frequency|number of buttons/.test(label)) return 3;
      if (/tracking method|colour|color/.test(label)) return 4;
      return 5;
    };
    rankedFacts.sort((left, right) => rank(left) - rank(right));
    const selectedFacts = [];
    const factBudget = overview.length ? 180 : 420;
    for (const fact of rankedFacts) {
      if (fact.length > 180) continue;
      if (selectedFacts.join('; ').length + fact.length <= factBudget) selectedFacts.push(fact.replace(/[.;]$/, ''));
      if (selectedFacts.length === (overview.length ? 2 : 4)) break;
    }
    if (selectedFacts.length) overview.push(selectedFacts.map(describeFact).join(' '));
    return overview.join(' ');
  };

  const initDescriptions = () => {
    document.querySelectorAll('[data-catalog-description]').forEach((description) => {
      const productId = description.dataset.productId;
      const sources = Array.from(document.querySelectorAll('[data-product-html-product-id]'))
        .filter((source) => source.dataset.productHtmlProductId === productId);
      const fallback = description.querySelector('[data-catalog-description-fallback]');
      // Match the actual HTML blocks, including their collection rules and chosen source.
      const summary = summarise(sources.length ? sources : [fallback.content], description.dataset.productTitle);
      const target = description.querySelector('[data-catalog-description-summary]');
      target.textContent = summary;
      target.hidden = !summary;
    });
  };

  const initBuildDescription = (section, caseTrigger) => {
    const description = section.querySelector('[data-pc-build-description]');
    if (!description) return;
    // Read saved trigger selections, never the component picker's unsaved draft.
    const triggers = new Map(Array.from(document.querySelectorAll('[data-a31-component-trigger]'))
      .map(trigger => [trigger.dataset.a31ComponentKey, trigger]));
    let groups = [];
    try {
      groups = JSON.parse(document.querySelector('[data-elite-pc-catalog]')?.textContent || 'null')?.componentGroups || [];
    } catch { /* Current trigger labels still provide an overview without catalogue facts. */ }
    const options = new Map(groups.map(group => [group.key, new Map(group.options.map(option => [option.sku, option]))]));
    const selected = (trigger) => (trigger?.dataset.eliteSelectedSku || '').split('|').filter(Boolean);
    const label = (trigger) => selected(trigger).length ? normalise(trigger.querySelector('.elite-pc-trigger__value')?.textContent || '') : '';
    const fact = (option, name) => normalise(String(option?.displaySpecs?.find(spec => spec.label.toLowerCase() === name.toLowerCase())?.value || ''));
    const update = () => {
      const cpu = label(triggers.get('processor'));
      const gpu = label(triggers.get('graphics'));
      const memoryTrigger = triggers.get('memory');
      const memoryParts = selected(memoryTrigger).map(sku => window.ElitePCPrepared?.option('ram',sku) || options.get('ram')?.get(sku));
      const knownCapacity = memoryParts.length && memoryParts.every(option => Number(fact(option, 'Total Capacity (GB)')) > 0);
      const ram = knownCapacity ? memoryParts.reduce((total, option) => total + Number(fact(option, 'Total Capacity (GB)')), 0)
        : memoryCapacityFromLabel(label(memoryTrigger));
      const performance = [];
      if (cpu && gpu) performance.push(`The ${cpu} processor handles game logic and everyday tasks, while the ${gpu} takes care of the graphics.`);
      else if (cpu) performance.push(`The ${cpu} processor is at the heart of this build, handling game logic and everyday tasks. Choose a graphics card to complete its gaming setup.`);
      else if (gpu) performance.push(`The ${gpu} is the graphics focus of this build. Choose a processor to complete its gaming setup.`);
      else performance.push('Choose a processor and graphics card to shape this PC around the games you play.');
      if (ram >= 32) performance.push(`With ${ram}GB of memory, there is extra headroom for keeping apps open alongside games and working on larger creative projects.`);
      else if (ram >= 16) performance.push(`Its ${ram}GB of memory gives games and everyday apps room to run together.`);
      else if (ram > 0) performance.push(`With ${ram}GB of memory, keeping background apps light leaves more room for your game.`);

      const ssdSkus = selected(triggers.get('ssd'));
      const nvme = ssdSkus.some(sku => options.get('nvme_ssd')?.has(sku)) || /NVMe/i.test(label(triggers.get('ssd')));
      const sata = ssdSkus.some(sku => options.get('sata_ssd')?.has(sku)) || /SATA/i.test(label(triggers.get('ssd')));
      const hdd = selected(triggers.get('hdd')).length > 0;
      const design = [];
      if (nvme) design.push('NVMe storage provides fast access to games and apps, helping reduce time spent waiting for files to load.');
      else if (sata) design.push('Solid-state storage keeps games and apps more responsive to load than a mechanical hard drive.');
      else if (hdd) design.push('Hard-drive storage provides space for a game library and media; adding an SSD would give games and apps a faster place to load from.');
      if (nvme && sata && hdd) design.push('Additional SSD and hard-drive storage leaves room to spread out your game library, media and larger files.');
      else if (nvme && sata) design.push('The additional SSD gives you a separate place for a growing game library.');
      else if (hdd && (nvme || sata)) design.push('The hard drive adds bulk storage, leaving the SSD for frequently used games and apps.');
      const caseOption = options.get('case')?.get(selected(caseTrigger)[0]);
      const caseType = fact(caseOption, 'Case Type');
      const caseBadge = caseTrigger?.closest('.a31-product')?.querySelector('[data-pc-case-type-badge]');
      if (caseBadge) {
        const badgeType = caseType || (label(caseTrigger) === section.dataset.pcCaseTitle ? caseBadge.dataset.pcCaseType : '') || '';
        const badgeLabel = caseBadge.querySelector('[data-pc-case-type-label]');
        if (badgeLabel.textContent !== badgeType || !badgeLabel.querySelector('.a31-case-type-line')) {
          const words = badgeType.split(/\s+/).filter(Boolean);
          const split = words.length > 2 && /\bglass case$/i.test(badgeType) ? words.length - 2 : Math.ceil(words.length / 2);
          const lines = [words.slice(0, split).join(' '), words.slice(split).join(' ')].filter(Boolean);
          badgeLabel.replaceChildren(...lines.flatMap((line, index) => {
            const span = document.createElement('span');
            span.className = 'a31-case-type-line';
            span.textContent = line;
            return index ? [document.createTextNode(' '), span] : [span];
          }));
        }
        caseBadge.hidden = !badgeType;
      }
      if (/wood/i.test(caseType)) design.push('The wood-fronted case gives the setup a more understated finish.');
      else if (/glass/i.test(caseType)) design.push('The glass-panel case puts the components on show.');
      else if (/airflow|mesh/i.test(caseType)) design.push('The airflow-focused case brings a ventilated finish to the build.');
      else if (label(caseTrigger)) design.push(`The ${label(caseTrigger)} gives this build its look.`);
      const write = (selector, copy) => {
        const target = description.querySelector(selector);
        if (target.textContent !== copy) target.textContent = copy;
        target.hidden = !copy;
      };
      write('[data-pc-description-performance]', performance.join(' '));
      write('[data-pc-description-design]', design.join(' '));
    };
    update();
    const observer = new MutationObserver(update);
    [caseTrigger, ...['processor', 'graphics', 'memory', 'ssd', 'hdd'].map(key => triggers.get(key))]
      .filter(Boolean).forEach(trigger => observer.observe(trigger, {
        childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['data-elite-selected-sku'],
      }));
  };

  const init = (scope = document) => {
    initDescriptions();
    scope.querySelectorAll('[data-elite-section-three]').forEach((section) => {
      if (section.dataset.eliteSectionThreeReady === 'true') return;
      section.dataset.eliteSectionThreeReady = 'true';

      if (section.hasAttribute('data-gaming-pc-information')) {
        const caseTrigger = document.querySelector('.a31-case-trigger');
        initBuildDescription(section, caseTrigger);
        const brandPrefixes = (section.dataset.pcCaseBrandPrefixes || '').split('|').filter(Boolean);
        const updateCaseBrand = () => {
          const title = normalise(caseTrigger?.querySelector('.elite-pc-trigger__value')?.textContent || section.dataset.pcCaseTitle || '');
          const prefix = brandPrefixes.find(brand => title.toLowerCase().startsWith(`${brand.toLowerCase()} `) || title.toLowerCase() === brand.toLowerCase());
          const brand = prefix === 'FRAME' ? 'Corsair' : prefix
            || (title === section.dataset.pcCaseTitle ? section.dataset.pcCaseBrand : 'Not specified');
          section.querySelectorAll('[data-pc-case-brand-value]').forEach(value => { value.textContent = brand; });
          section.querySelectorAll('[data-pc-description-case-title]').forEach(value => {
            if (value.textContent !== title) value.textContent = title;
          });
        };
        updateCaseBrand();
        if (caseTrigger) new MutationObserver(updateCaseBrand).observe(caseTrigger, {
          childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['data-elite-selected-sku'],
        });
      }

      section.querySelectorAll('[data-s3-jump]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const key = trigger.dataset.s3Jump;
          const tab = section.querySelector(`[data-s3-tab="${key}"]`);
          if (!tab) return;
          tab.click();
          tab.focus({ preventScroll: true });
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });

      section.querySelectorAll('[data-product-tab]').forEach((tab) => {
        tab.addEventListener('click', () => {
          tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        });
      });
    });
  };

  document.addEventListener('DOMContentLoaded', async () => {
    if(window.ElitePCData?.pending)await window.ElitePCData.ready;
    if(window.ElitePCData?.yield)await window.ElitePCData.yield();
    init();
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
